import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { mget, mpost, errMsg, todayISO } from "@/manager/lib";
import { PageHeader, Field, goldBtn } from "@/manager/ui";
import TaskList from "@/manager/components/TaskList";

const CATS = ["general", "catering", "personal", "limpieza", "bebidas", "montaje", "cobro", "cliente", "proveedores"];

function NewTaskDialog({ open, onClose, onSaved }) {
  const [f, setF] = useState({ title: "", due_date: todayISO(), priority: "media", category: "general", assignee: "", event_id: "" });
  const [events, setEvents] = useState([]);
  useEffect(() => { if (open) mget("/events", { start: todayISO() }).then(setEvents); }, [open]);
  const save = async (e) => {
    e.preventDefault();
    try { await mpost("/tasks", { ...f, event_id: f.event_id || null }); toast.success("Tarea creada"); onSaved(); onClose(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="task-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Nueva tarea</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <Field label="Título *"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required data-testid="task-title" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Fecha límite"><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="task-due" /></Field>
            <Field label="Responsable"><Input value={f.assignee} onChange={(e) => setF({ ...f, assignee: e.target.value })} data-testid="task-assignee" /></Field>
            <Field label="Prioridad">
              <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}><SelectTrigger data-testid="task-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{["alta", "media", "baja"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
            </Field>
            <Field label="Categoría">
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger data-testid="task-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            </Field>
          </div>
          <Field label="Evento (opcional)">
            <Select value={f.event_id || "none"} onValueChange={(v) => setF({ ...f, event_id: v === "none" ? "" : v })}><SelectTrigger data-testid="task-event"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">Sin evento</SelectItem>{events.map((e) => <SelectItem key={e.id} value={e.id}>{e.date} · {e.title}</SelectItem>)}</SelectContent></Select>
          </Field>
          <DialogFooter><Button type="submit" className={goldBtn} data-testid="task-save">Crear</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Tasks() {
  const [params, setParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [status, setStatus] = useState("abiertas");
  const [cat, setCat] = useState("all");
  const load = useCallback(() => mget("/tasks", { status: status === "todas" ? undefined : status, category: cat === "all" ? undefined : cat }).then(setTasks), [status, cat]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="manager-tasks">
      <PageHeader eyebrow="Tareas" title="Lo que hay que hacer">
        <Button className={goldBtn} onClick={() => setParams({ new: "1" })} data-testid="tasks-new"><Plus size={15} /> Nueva tarea</Button>
      </PageHeader>
      <div className="flex flex-wrap gap-3 mb-4">
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-44 bg-white" data-testid="tasks-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>{[["abiertas", "Abiertas"], ["completada", "Completadas"], ["no_aplica", "No aplica"], ["todas", "Todas"]].map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
        <Select value={cat} onValueChange={setCat}><SelectTrigger className="w-44 bg-white" data-testid="tasks-filter-cat"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todas las categorías</SelectItem>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
      </div>
      <TaskList tasks={tasks} reload={load} showEvent />
      <NewTaskDialog open={params.get("new") === "1"} onClose={() => setParams({})} onSaved={load} />
    </div>
  );
}
